<template>
  <div>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add Investment
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.user_id"
                :items="users"
                outlined
                label="Select User"
                prepend-inner-icon="mdi-account"
                :error-messages="errors.user_id"
                item-text="text"
                item-value="value"
                :loading="loadingMasters"
                clearable
                :disabled="loadingMasters"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.product_id"
                :items="products"
                outlined
                label="Select Product"
                prepend-inner-icon="mdi-cube-outline"
                :error-messages="errors.product_id"
                item-text="text"
                item-value="value"
                :loading="loadingMasters"
                clearable
                :disabled="loadingMasters"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.invested_amount"
                outlined
                label="Investment Amount"
                prepend-inner-icon="mdi-currency-usd"
                :error-messages="errors.invested_amount"
                type="number"
                min="0"
                step="0.01"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.payment_mode"
                :items="paymentModes"
                outlined
                label="Payment Mode"
                prepend-inner-icon="mdi-credit-card"
                :error-messages="errors.payment_mode"
                item-text="text"
                item-value="value"
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Image Upload Fields for Online Payment -->
          <v-row v-if="form.payment_mode == 2">
            <v-col sm="6">
              <v-file-input
                v-model="form.upi_screenshot"
                outlined
                label="UPI Screenshot"
                prepend-inner-icon="mdi-credit-card"
                :error-messages="errors.upi_screenshot"
                accept="image/*,application/pdf"
                :show-size="true"
                clearable
              ></v-file-input>
            </v-col>
            <v-col sm="6">
              <v-file-input
                v-model="form.bank_statement"
                outlined
                label="Bank Statement"
                prepend-inner-icon="mdi-bank"
                :error-messages="errors.bank_statement"
                accept="image/*,application/pdf"
                :show-size="true"
                clearable
              ></v-file-input>
            </v-col>
          </v-row>

          <v-divider class="my-5"> </v-divider>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Save Investment
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Snackbar for error/success messages -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar.show = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>
<script>
import axios from "axios";
export default {
  name: "InvestmentCreatePage",
  data() {
    return {
      form: {
        product_id: "",
        user_id: "",
        invested_amount: "",
        payment_mode: "",
        upi_screenshot: null,
        bank_statement: null,
      },
      users: [],
      products: [],
      paymentModes: [
        { text: "Wallet", value: 1 },
        { text: "Online", value: 2 },
        { text: "Cash", value: 3 },
      ],
      isLoading: false,
      loadingMasters: false,
      errors: {},
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    };
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        this.loadingMasters = true;
        const response = await axios.get('masters?context=investments');
        
        if (response.data?.users) {
          this.users = response.data.users.map(user => ({
            text: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || `User ${user.id}`,
            value: user.id,
          }));
        }
        
        if (response.data?.products) {
          this.products = response.data.products.map(product => ({
            text: product.product_name || `Product ${product.id}`,
            value: product.id,
          }));
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        this.showSnackbar('Failed to load dropdown data. Please refresh the page.', 'error');
      } finally {
        this.loadingMasters = false;
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;  
      this.snackbar.show = true;
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {}; // Clear previous errors
        
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('product_id', this.form.product_id);
        formData.append('user_id', this.form.user_id);
        formData.append('invested_amount', this.form.invested_amount);
        formData.append('payment_mode', this.form.payment_mode);
        
        // Add image files if payment mode is online (2)
        if (this.form.payment_mode == 2) {
          if (this.form.upi_screenshot) {
            formData.append('upi_screenshot', this.form.upi_screenshot);
          }
          if (this.form.bank_statement) {
            formData.append('bank_statement', this.form.bank_statement);
          }
        }
        
        await axios.post("/investments", formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        await this.saveUserTimestamp(null, this.form);
        this.showSnackbar('Investment created successfully!', 'success');
        this.isLoading = false;
        
        // Navigate back after a short delay to show the success message
        setTimeout(() => {
          this.$router.back();
        }, 1000);
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        
        // Handle validation errors
        if (e.response && e.response.status === 422) {
          this.errors = e.response.data.errors || {};
          this.showSnackbar('Please check the form for validation errors.', 'error');
        } else if (e.response && e.response.data) {
          // Show API error message
          const message = e.response.data.message || 'Failed to create investment';
          this.showSnackbar(message, 'error');
        } else {
          this.showSnackbar('Failed to create investment. Please try again.', 'error');
        }
      }
    },
  },
};
</script>
